<script setup lang="ts">
import STaskGradePill from '../../../components/STaskGradePill.vue';
import SBadge from '../../../design-system/SBadge.vue';
import STooltip from '../../../design-system/STooltip.vue';
import SLink from '../../../components/SLink.vue';
import FeedbackByPart from './FeedbackByPart.vue';
import {TaskFeedback, TaskResponse, TaskState} from '../../../types/entities/tasks';
import {Grade} from '../../../types/entities/base-grade';
import SIcon from '../../../design-system/SIcon.vue';
import GradeDisplay = App.Enums.GradeDisplay;
import {useI18n} from 'vue-i18n';
import TaskCriterionGradeDto = App.DTOs.Entities.Grades.TaskCriterionGradeDto;
import {computed} from 'vue';
import {TaskResponseState, useTaskStateHelpers} from './task-response-state-utils';

const props = withDefaults(
  defineProps<{
    activeGradeUrl?: string;
    generalFeedbackOnly?: boolean;
    attemptedTaskIds: number[];
    draftTaskIds: number[];
    studentAssignmentTaskGrades: Grade[];

    // Response
    storedResponse: TaskResponse | null;
    responseGrade: Grade | null;
    partGrades: TaskCriterionGradeDto[];
    feedbackByPart: Record<string, TaskFeedback[]>;
    taskState: TaskState | null;

    taskWeight: number;
    gradeDisplay: GradeDisplay;
  }>(),
  {
    attemptedTaskIds: () => [] as number[],
    draftTaskIds: () => [] as number[],
    studentAssignmentTaskGrades: () => [] as Grade[],
    gradeDisplay: 'FRACTION' as GradeDisplay,
  }
);

const {t} = useI18n({
  inheritLocale: true,
  useScope: 'local',
});

const generalFeedback = computed(() => {
  return (props.feedbackByPart['default'] ?? []).filter((feedback) => !feedback.blockId);
});

const isTaskResponseEffective = computed(() => {
  return !!props.responseGrade?.isEffective;
});

const hasGradingFailed = computed(() => {
  return props.storedResponse?.gradingStatus === 'failed';
});

const {determineTaskResponseState} = useTaskStateHelpers(props);
const feedbackState = computed(
  (): TaskResponseState => determineTaskResponseState(props.storedResponse, props.responseGrade)
);

const feedbackClasses: Record<TaskResponseState, string> = {
  draft: 'border-gray-300/30 from-gray-100/80 via-gray-100/40 to-gray-50/0 divide-black/10',
  ungraded: 'border-gray-300 from-gray-150/80 via-gray-50/70 to-gray-50/0 divide-black/10',
  incorrect: 'border-red-200 from-red-200 via-red-100/50 to-red-50/0 divide-red-300/60',
  partial: 'border-blue-100/80 from-blue-100/70 via-blue-100/20 to-blue-50/0 divide-blue-100/80',
  correct:
    'border-green-300/70 from-green-200/70 via-green-100/50 to-green-50/0 divide-green-300/70',
  incomplete: 'border-gray-300/30 from-gray-100/80 via-gray-100/40 to-gray-50/0 divide-black/10',
};
</script>

<template>
  <section aria-live="polite" class="flex flex-col gap-3 w-full">
    <span class="flex drop-shadow-card-soft">
      <s-tooltip v-if="activeGradeUrl && !isTaskResponseEffective" wrapperClass="flex">
        <template #tooltip>
          {{ t('thisIsNotTheActiveGradeTooltip') }}
        </template>
        <template #default="{tooltipId}">
          <s-link :href="activeGradeUrl" class="flex" :aria-describedby="tooltipId">
            <s-badge
              color="yellow"
              size="lg"
              :class="{
                '!rounded-r-none': !!responseGrade || hasGradingFailed,
              }"
              class="rounded-l-full"
            >
              <s-icon name="alert" size="20" class="opacity-70" />
              {{ t('inactive') }}
            </s-badge>
          </s-link>
        </template>
      </s-tooltip>
      <s-task-grade-pill
        v-if="responseGrade"
        :grade="responseGrade"
        :grade-display="gradeDisplay"
        :weight="taskWeight"
        :badge-class="
          (activeGradeUrl && !isTaskResponseEffective ? '!rounded-l-none' : '!rounded-l-full') +
          (storedResponse?.gradingStatus === 'failed' ? ' !rounded-r-none' : ' !rounded-r-full')
        "
      />
      <s-badge
        v-if="storedResponse?.gradingStatus === 'failed'"
        color="red"
        :class="{
          '!rounded-l-none': !!responseGrade,
        }"
      >
        <s-icon name="warning" size="16" class="opacity-70" />
        {{ t('autogradingFailed') }}
      </s-badge>
    </span>
    <div
      v-if="
        responseGrade?.submittedByName ||
        (generalFeedbackOnly && generalFeedback.length > 0) ||
        (!generalFeedbackOnly && Object.keys(feedbackByPart).length > 0)
      "
      class="flex-none relative rounded-lg shadow-card-soft border overflow-hidden flex flex-col flex-1 w-full bg-gradient-to-r leading-snug text-black/80 divide-y"
      :class="feedbackClasses[feedbackState]"
    >
      <feedback-by-part
        v-if="
          (generalFeedbackOnly && generalFeedback.length > 0) ||
          (!generalFeedbackOnly && Object.keys(feedbackByPart).length > 0)
        "
        :feedback-by-part="generalFeedbackOnly ? {default: generalFeedback} : feedbackByPart"
        :use-html-newlines="responseGrade?.isManuallyEntered"
        :part-grades="partGrades"
        :task-state="taskState"
        class="px-4 py-2.5"
      />
      <div
        v-if="responseGrade?.submittedByName"
        class="text-gray-600 font-medium text-sm leading-tight italic px-4 py-2 bg-gradient-to-r from-white/50 via-white/30 to-white/0"
      >
        {{ t('gradeManuallyEnteredBy') }}
        <span class="font-bold text-gray-800">{{ responseGrade.submittedByName }}</span>
      </div>
    </div>
  </section>
</template>
<i18n>
{
  "en": {
    "autogradingFailed": "Autograding Failed",
    "thisIsTheActiveGrade": "This is the active grade",
    "thisIsNotTheActiveGradeTooltip": "This is not the active grade. Click to view the active grade.",
  },
  "fr": {
    "autogradingFailed": "Échec de l'évaluation automatique",
    "thisIsTheActiveGrade": "Cette est la note active",
    "thisIsNotTheActiveGradeTooltip": "Cette n'est pas la note active. Cliquer pour voir la note active.",
  }
}
</i18n>
